import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Settings, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  X,
  Globe,
  Calendar,
  Building2,
  Shield,
  Palette,
  Mail,
  Bell,
  Edit3,
  Plus,
  Key,
  RefreshCw,
  Eye,
  EyeOff,
  Star,
  Trash2,
  Loader2
} from 'lucide-react';

// Custom Modal Component
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", isLoading = false, type = "danger" }) => {
  if (!isOpen) return null;
  
  const buttonColors = {
    danger: "bg-red-600 hover:bg-red-700",
    success: "bg-green-600 hover:bg-green-700",
    primary: "bg-blue-600 hover:bg-blue-700"
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 ${buttonColors[type]}`}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Result Modal Component
const ResultModal = ({ isOpen, onClose, title, message, type = "success" }) => {
  if (!isOpen) return null;
  
  const icons = {
    success: <CheckCircle className="h-12 w-12 text-green-500" />,
    error: <AlertCircle className="h-12 w-12 text-red-500" />,
    info: <AlertCircle className="h-12 w-12 text-blue-500" />
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center animate-in fade-in zoom-in duration-200">
        <div className="flex justify-center mb-4">{icons[type]}</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-2 text-sm text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors"
        >
          OK
        </button>
      </div>
    </div>
  );
};

const CMSSettings = () => {
  const { user, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('google');
  
  // Modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [resultModal, setResultModal] = useState({ isOpen: false, title: '', message: '', type: 'success' });
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [settings, setSettings] = useState({
    google_sync_enabled: true,
    google_business_sync_enabled: true,
    google_calendar_sync_enabled: true,
    google_reviews_sync_enabled: false,
    google_api_key: '',
    google_place_id: '',
    google_reviews_api_method: 'simple',  // 'simple' or 'oauth'
    google_oauth_client_id: '',
    google_oauth_client_secret: '',
    google_account_id: '',
    google_location_id: '',
    nexsite_api_key: ''
  });
  
  // AWS Settings state
  const [awsSettings, setAwsSettings] = useState({
    access_key_id: '',
    secret_access_key: '',
    region: 'us-east-2',
    bucket_name: 'prime-pixel-cms-uploads',
    root_folder: 'ah/pvah1000/',
    ecommerce_folder: 'ah/0ecom/',
    max_file_size: 52428800, // 50MB
    enabled: false,
    recycle_bin_enabled: true,
    recycle_bin_auto_delete_days: 30
  });
  
  const [showApiKeys, setShowApiKeys] = useState({
    aws_access_key: false,
    aws_secret_key: false,
    google_oauth_client_secret: false,
    google_api_key: false,
    nexsite_api_key: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pageLoading, setPageLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null); // 'connected', 'disconnected', 'testing'
  
  // Feature Flags settings state
  const [appearanceSettings, setAppearanceSettings] = useState({
    urgent_care_enabled: true,
    online_ordering_enabled: true,
    online_appointments_enabled: true,
    services_enabled: true,
    boarding_enabled: true,
    records_enabled: true,
    hr_enabled: true,
    ai_costs_enabled: true,
    system_settings_enabled: true
  });

  // Global placeholders state
  const [placeholders, setPlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [editingPlaceholder, setEditingPlaceholder] = useState(null);
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [placeholderForm, setPlaceholderForm] = useState({
    name: '',
    placeholder: '',
    value: '',
    description: ''
  });

  // Other settings state (Session Time)
  const [otherSettings, setOtherSettings] = useState({
    enable_session_time: true,
    initial_session_time: 10, // in minutes
    extended_session_time: 10 // in minutes
  });

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const tabs = [
    { 
      id: 'google', 
      label: 'Google', 
      icon: Globe,
      color: 'var(--theme-primary)'
    },
    {
      id: 'aws',
      label: 'AWS',
      icon: Key,
      color: '#ff6b35'
    },
    {
      id: 'api-keys',
      label: 'API Keys',
      icon: Key,
      color: '#8b5cf6'
    },
    {
      id: 'placeholders',
      label: 'Placeholders',
      icon: Settings,
      color: '#f59e0b'
    },
    { 
      id: 'appearance', 
      label: 'Feature Flags', 
      icon: Palette,
      color: '#7c3aed'
    },
    { 
      id: 'other', 
      label: 'Other Settings', 
      icon: Settings,
      color: '#10b981'
    },
    { 
      id: 'notifications', 
      label: 'Notifications', 
      icon: Bell,
      color: '#059669'
    }
  ];

  // Fetch CMS settings
  const fetchSettings = async () => {
    setLoading(true);
    try {
      // Fetch CMS settings
      const cmsResponse = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (cmsResponse.ok) {
        const cmsData = await cmsResponse.json();
        setSettings({
          google_sync_enabled: cmsData.google_sync_enabled,
          google_business_sync_enabled: cmsData.google_business_sync_enabled,
          google_calendar_sync_enabled: cmsData.google_calendar_sync_enabled,
          google_reviews_sync_enabled: cmsData.google_reviews_sync_enabled || false,
          google_api_key: cmsData.google_api_key || '',
          google_place_id: cmsData.google_place_id || '',
          google_reviews_api_method: cmsData.google_reviews_api_method || 'simple',
          google_oauth_client_id: cmsData.google_oauth_client_id || '',
          google_oauth_client_secret: cmsData.google_oauth_client_secret || '',
          google_account_id: cmsData.google_account_id || '',
          google_location_id: cmsData.google_location_id || '',
          nexsite_api_key: cmsData.nexsite_api_key || ''
        });
        
        // Load AWS settings from CMS settings
        setAwsSettings({
          access_key_id: cmsData.aws_access_key_id || '',
          secret_access_key: cmsData.aws_secret_access_key || '',
          region: cmsData.aws_region || 'us-east-2',
          bucket_name: cmsData.aws_bucket_name || 'prime-pixel-cms-uploads',
          root_folder: cmsData.aws_root_folder || 'ah/pvah1000/',
          ecommerce_folder: cmsData.aws_ecommerce_folder || 'ah/0ecom/',
          max_file_size: cmsData.aws_max_file_size || 52428800,
          enabled: cmsData.aws_enabled || false,
          recycle_bin_enabled: cmsData.recycle_bin_enabled ?? true,
          recycle_bin_auto_delete_days: cmsData.recycle_bin_auto_delete_days ?? 30
        });
        
        // Set connection status based on whether AWS is enabled
        if (cmsData.aws_enabled && cmsData.aws_access_key_id && cmsData.aws_secret_access_key) {
          setConnectionStatus(null); // Unknown until tested
        } else {
          setConnectionStatus('disconnected');
        }
        
        // Load feature flags settings
        setAppearanceSettings({
          urgent_care_enabled: cmsData.urgent_care_enabled !== undefined ? cmsData.urgent_care_enabled : true,
          online_ordering_enabled: cmsData.online_ordering_enabled !== undefined ? cmsData.online_ordering_enabled : true,
          online_appointments_enabled: cmsData.online_appointments_enabled !== undefined ? cmsData.online_appointments_enabled : true,
          services_enabled: cmsData.services_enabled !== undefined ? cmsData.services_enabled : true,
          boarding_enabled: cmsData.boarding_enabled !== undefined ? cmsData.boarding_enabled : true,
          records_enabled: cmsData.records_enabled !== undefined ? cmsData.records_enabled : true,
          hr_enabled: cmsData.hr_enabled !== undefined ? cmsData.hr_enabled : true,
          ai_costs_enabled: cmsData.ai_costs_enabled !== undefined ? cmsData.ai_costs_enabled : true,
          system_settings_enabled: cmsData.system_settings_enabled !== undefined ? cmsData.system_settings_enabled : true
        });
        
        // Load other settings (PDF Tools Session Settings)
        setOtherSettings({
          enable_session_time: cmsData.enable_session_time !== undefined ? cmsData.enable_session_time : true,
          initial_session_time: cmsData.initial_session_time || 10,
          extended_session_time: cmsData.extended_session_time || 10
        });
      } else if (cmsResponse.status === 403) {
        setMessage({ type: 'error', text: 'Access denied. Admin privileges required.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch CMS settings.' });
      }

    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  // Authentication useEffect
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (token && !user) {
      return;
    }

    if (!token || !user) {
      setPageLoading(false);
      return;
    }

    if (user.role !== 'admin') {
      setPageLoading(false);
      return;
    }

    // Admin user - fetch settings
    fetchSettings();
  }, [token, user, authLoading]);

  // Load placeholders when placeholders tab is active
  useEffect(() => {
    if (activeTab === 'placeholders' && user && user.role === 'admin') {
      fetchPlaceholders();
    }
  }, [activeTab, user, token]);

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showPlaceholderModal) {
        handleCancelPlaceholderForm();
      }
    };

    if (showPlaceholderModal) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showPlaceholderModal]);

  // Check if user is admin
  if (!authLoading && (!user || user.role !== 'admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only administrators can access CMS Settings.</p>
        </div>
      </div>
    );
  }

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading CMS Settings...</p>
        </div>
      </div>
    );
  }

  // Save Google settings only (AWS settings handled by Connect/Disconnect buttons)
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Save only Google settings, preserve existing AWS settings
      const cmsResponse = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings) // Only save Google sync settings
      });

      if (cmsResponse.ok) {
        setMessage({ type: 'success', text: 'Google settings saved successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to save Google settings. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Save feature flags settings
  const handleSaveAppearanceSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(appearanceSettings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Feature flags saved successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to save feature flags. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving feature flags:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Save other settings (PDF Tools Session Settings)
  const handleSaveOtherSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(otherSettings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Other settings saved successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to save other settings. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving other settings:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Save API Keys
  const handleSaveApiKeys = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nexsite_api_key: settings.nexsite_api_key
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'API Keys saved successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to save API Keys. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving API Keys:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Test S3 Connection
  const testS3Connection = async () => {
    setSaving(true);
    setConnectionStatus('testing');
    setMessage({ type: '', text: '' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/s3/test-connection`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setConnectionStatus('connected');
        setMessage({ 
          type: 'success', 
          text: `✅ S3 Connection Successful! Connected to bucket: ${data.bucket} in region: ${data.region}` 
        });
      } else {
        setConnectionStatus('disconnected');
        setMessage({ 
          type: 'error', 
          text: `❌ S3 Connection Failed: ${data.message}` 
        });
      }
    } catch (error) {
      setConnectionStatus('disconnected');
      setMessage({ 
        type: 'error', 
        text: `❌ Connection Test Failed: ${error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  // Connect to S3 - Save settings and test connection
  const handleConnect = async () => {
    setSaving(true);
    setConnectionStatus('testing');
    setMessage({ type: '', text: '' });

    try {
      // First save the settings with AWS enabled
      const connectSettings = {
        ...settings,
        aws_access_key_id: awsSettings.access_key_id,
        aws_secret_access_key: awsSettings.secret_access_key,
        aws_region: awsSettings.region,
        aws_bucket_name: awsSettings.bucket_name,
        aws_root_folder: awsSettings.root_folder,
        aws_ecommerce_folder: awsSettings.ecommerce_folder,
        aws_max_file_size: awsSettings.max_file_size,
        aws_enabled: true, // Enable AWS when connecting
        recycle_bin_enabled: awsSettings.recycle_bin_enabled,
        recycle_bin_auto_delete_days: awsSettings.recycle_bin_auto_delete_days
      };

      const saveResponse = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(connectSettings)
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save AWS settings');
      }

      // Update local state
      setAwsSettings(prev => ({ ...prev, enabled: true }));

      // Test the connection
      const testResponse = await fetch(`${API_BASE_URL}/api/s3/test-connection`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      const testData = await testResponse.json();
      
      if (testData.success) {
        setConnectionStatus('connected');
        setMessage({ 
          type: 'success', 
          text: `✅ Connected Successfully! Connected to bucket: ${testData.bucket} in region: ${testData.region}` 
        });
      } else {
        setConnectionStatus('disconnected');
        // Disable AWS if connection failed
        setAwsSettings(prev => ({ ...prev, enabled: false }));
        setMessage({ 
          type: 'error', 
          text: `❌ Connection Failed: ${testData.message}` 
        });
      }

    } catch (error) {
      setConnectionStatus('disconnected');
      setAwsSettings(prev => ({ ...prev, enabled: false }));
      setMessage({ 
        type: 'error', 
        text: `❌ Connection Failed: ${error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  // Disconnect from S3 - Disable AWS settings
  const handleDisconnect = async () => {
    setSaving(true);
    setConnectionStatus('testing');
    setMessage({ type: '', text: '' });

    try {
      // Save settings with AWS disabled
      const disconnectSettings = {
        ...settings,
        aws_access_key_id: awsSettings.access_key_id,
        aws_secret_access_key: awsSettings.secret_access_key,
        aws_region: awsSettings.region,
        aws_bucket_name: awsSettings.bucket_name,
        aws_root_folder: awsSettings.root_folder,
        aws_ecommerce_folder: awsSettings.ecommerce_folder,
        aws_max_file_size: awsSettings.max_file_size,
        aws_enabled: false, // Disable AWS when disconnecting
        recycle_bin_enabled: awsSettings.recycle_bin_enabled,
        recycle_bin_auto_delete_days: awsSettings.recycle_bin_auto_delete_days
      };

      const response = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(disconnectSettings)
      });

      if (response.ok) {
        setAwsSettings(prev => ({ ...prev, enabled: false }));
        setConnectionStatus('disconnected');
        setMessage({ 
          type: 'success', 
          text: '✅ Successfully Disconnected from AWS S3' 
        });
      } else {
        throw new Error('Failed to disconnect');
      }

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `❌ Disconnect Failed: ${error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle toggle change
  const handleToggleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Global placeholders functions
  const fetchPlaceholders = async () => {
    setLoadingPlaceholders(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/global-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlaceholders(data);
      } else {
        throw new Error('Failed to fetch placeholders');
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load global placeholders' });
      console.error('Error fetching placeholders:', err);
    } finally {
      setLoadingPlaceholders(false);
    }
  };

  const handleCreatePlaceholder = async () => {
    if (!placeholderForm.name.trim() || !placeholderForm.placeholder.trim() || !placeholderForm.value.trim()) {
      setMessage({ type: 'error', text: 'Name, placeholder, and value are required' });
      return;
    }

    // Auto-format placeholder if it doesn't have brackets
    let formattedPlaceholder = placeholderForm.placeholder;
    if (!formattedPlaceholder.startsWith('[') || !formattedPlaceholder.endsWith(']')) {
      formattedPlaceholder = `[${formattedPlaceholder.toUpperCase()}]`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/global-placeholders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...placeholderForm,
          placeholder: formattedPlaceholder,
          created_by: user?.email || 'unknown'
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Global placeholder created successfully!' });
        setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
        setShowPlaceholderModal(false);
        fetchPlaceholders();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create placeholder');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleUpdatePlaceholder = async () => {
    if (!placeholderForm.name.trim() || !placeholderForm.placeholder.trim() || !placeholderForm.value.trim()) {
      setMessage({ type: 'error', text: 'Name, placeholder, and value are required' });
      return;
    }

    // Auto-format placeholder if it doesn't have brackets
    let formattedPlaceholder = placeholderForm.placeholder;
    if (!formattedPlaceholder.startsWith('[') || !formattedPlaceholder.endsWith(']')) {
      formattedPlaceholder = `[${formattedPlaceholder.toUpperCase()}]`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/global-placeholders/${editingPlaceholder.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...placeholderForm,
          placeholder: formattedPlaceholder
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Global placeholder updated successfully!' });
        setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
        setEditingPlaceholder(null);
        setShowPlaceholderModal(false);
        fetchPlaceholders();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update placeholder');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleDeletePlaceholder = async (placeholderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/global-placeholders/${placeholderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Global placeholder deleted successfully!' });
        fetchPlaceholders();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete placeholder');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const confirmDeletePlaceholder = (placeholderId, placeholderName) => {
    setConfirmMessage(`Are you sure you want to delete the global placeholder "${placeholderName}"? This action cannot be undone.`);
    setConfirmAction(() => () => handleDeletePlaceholder(placeholderId));
    setShowConfirmModal(true);
  };

  const handleEditPlaceholder = (placeholder) => {
    console.log('Edit button clicked for placeholder:', placeholder);
    // Clear any existing messages first
    setMessage({ type: '', text: '' });
    
    setEditingPlaceholder(placeholder);
    setPlaceholderForm({
      name: placeholder.name,
      placeholder: placeholder.placeholder,
      value: placeholder.value,
      description: placeholder.description || ''
    });
    setShowPlaceholderModal(true);
    
    console.log('State updated - modal should show');
  };

  const handleCancelPlaceholderForm = () => {
    console.log('Cancel button clicked');
    setMessage({ type: '', text: '' });
    setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
    setEditingPlaceholder(null);
    setShowPlaceholderModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Main Card Container */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Inside the main card */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">CMS Settings</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-lg p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      onClick={() => setMessage({ type: '', text: '' })}
                      className={`inline-flex ${
                        message.type === 'error' ? 'text-red-400 hover:text-red-600' : 'text-green-400 hover:text-green-600'
                      }`}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation - Styled like Business Info */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
            
            {/* Google Integration Tab */}
            {activeTab === 'google' && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                  <div className="mb-3 sm:mb-0">
                    <h2 className="text-base font-semibold text-gray-900">Google Integration Settings</h2>
                    <p className="text-xs text-gray-600">
                      Configure Google services integration across the application
                    </p>
                  </div>
                  
                  {/* Save Button */}
                  <button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className="w-full sm:w-auto text-white px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50 flex items-center justify-center sm:justify-start font-medium"
                    style={{ 
                      backgroundColor: saving ? '#94a3b8' : '#10b981'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) e.target.style.backgroundColor = '#059669';
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) e.target.style.backgroundColor = '#10b981';
                    }}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>

                <div className="space-y-3">
                  
                  {/* Master Google Sync Toggle */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200">
                    <div className="flex items-center space-x-2.5">
                      <div className="bg-blue-100 rounded-full p-1.5">
                        <Globe className="h-4 w-4 text-theme-blue" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm text-gray-900">Master Google Sync</h3>
                        <p className="text-xs text-gray-600">
                          Enable all Google integrations across the platform
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.google_sync_enabled}
                        onChange={(e) => handleToggleChange('google_sync_enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme-blue"></div>
                    </label>
                  </div>

                  {/* Google Business Profile Sync */}
                  <div className={`flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 transition-opacity ${
                    !settings.google_sync_enabled ? 'opacity-50' : ''
                  }`}>
                    <div className="flex items-center space-x-2.5">
                      <div className="bg-green-100 rounded-full p-1.5">
                        <Building2 className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm text-gray-900">Google Business Profile Sync</h3>
                        <p className="text-xs text-gray-600">
                          Sync business information with Google Business Profile
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.google_business_sync_enabled && settings.google_sync_enabled}
                        onChange={(e) => handleToggleChange('google_business_sync_enabled', e.target.checked)}
                        disabled={!settings.google_sync_enabled}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"></div>
                    </label>
                  </div>

                  {/* Google Calendar Sync */}
                  <div className={`flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 transition-opacity ${
                    !settings.google_sync_enabled ? 'opacity-50' : ''
                  }`}>
                    <div className="flex items-center space-x-2.5">
                      <div className="bg-purple-100 rounded-full p-1.5">
                        <Calendar className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm text-gray-900">Google Calendar Sync</h3>
                        <p className="text-xs text-gray-600">
                          Sync appointments and events with Google Calendar
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.google_calendar_sync_enabled && settings.google_sync_enabled}
                        onChange={(e) => handleToggleChange('google_calendar_sync_enabled', e.target.checked)}
                        disabled={!settings.google_sync_enabled}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 disabled:cursor-not-allowed disabled:opacity-50"></div>
                    </label>
                  </div>

                  {/* Google Reviews Sync */}
                  <div className={`flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 transition-opacity ${
                    !settings.google_sync_enabled ? 'opacity-50' : ''
                  }`}>
                    <div className="flex items-center space-x-2.5">
                      <div className="bg-yellow-100 rounded-full p-1.5">
                        <Star className="h-4 w-4 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm text-gray-900">Google Reviews Sync</h3>
                        <p className="text-xs text-gray-600">
                          Display Google reviews dynamically on your website
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.google_reviews_sync_enabled && settings.google_sync_enabled}
                        onChange={(e) => handleToggleChange('google_reviews_sync_enabled', e.target.checked)}
                        disabled={!settings.google_sync_enabled}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500 disabled:cursor-not-allowed disabled:opacity-50"></div>
                    </label>
                  </div>

                </div>

                {/* Google API Configuration */}
                <div className="space-y-3 mt-4">
                  <h3 className="text-sm font-semibold text-gray-900">Google API Configuration</h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Configure Google Places API credentials to enable reviews and other features
                  </p>

                  {/* Google API Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <Key className="h-3.5 w-3.5 inline mr-1.5" />
                      Google Places API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKeys.google_api_key ? "text" : "password"}
                        value={settings.google_api_key}
                        onChange={(e) => setSettings(prev => ({ ...prev, google_api_key: e.target.value }))}
                        placeholder="Enter your Google Places API key"
                        className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKeys(prev => ({ ...prev, google_api_key: !prev.google_api_key }))}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showApiKeys.google_api_key ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Get your API key from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">Google Cloud Console</a>
                    </p>
                  </div>

                  {/* Google Place ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <Building2 className="h-3.5 w-3.5 inline mr-1.5" />
                      Google Place ID
                    </label>
                    <input
                      type="text"
                      value={settings.google_place_id}
                      onChange={(e) => setSettings(prev => ({ ...prev, google_place_id: e.target.value }))}
                      placeholder="Enter your Google Place ID (e.g., ChIJN1t_tDeuEmsRUsoyG83frY4)"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Find your Place ID using <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">Google Place ID Finder</a>
                    </p>
                  </div>

                  {/* Delete All Reviews */}
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <h3 className="text-sm font-semibold text-red-800 mb-2">Delete All Reviews</h3>
                    <p className="text-xs text-red-600 mb-3">
                      Clear all existing reviews from the database. Use this when changing to a new business Place ID.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmModal({
                          isOpen: true,
                          title: 'Delete All Reviews?',
                          message: 'This will permanently delete all reviews from the database. This action cannot be undone.',
                          onConfirm: async () => {
                            setIsDeleting(true);
                            try {
                              const authToken = localStorage.getItem('token');
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/reviews/delete-all`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${authToken}` }
                              });
                              const data = await response.json();
                              setConfirmModal({ isOpen: false });
                              if (data.success) {
                                setResultModal({
                                  isOpen: true,
                                  type: 'success',
                                  title: 'Reviews Deleted',
                                  message: `Successfully deleted ${data.deleted_count || 0} reviews (Google: ${data.google_reviews_deleted || 0}, Manual: ${data.manual_reviews_deleted || 0})`
                                });
                              } else {
                                setResultModal({
                                  isOpen: true,
                                  type: 'error',
                                  title: 'Error',
                                  message: data.error || 'Failed to delete reviews'
                                });
                              }
                            } catch (error) {
                              setConfirmModal({ isOpen: false });
                              setResultModal({
                                isOpen: true,
                                type: 'error',
                                title: 'Error',
                                message: error.message
                              });
                            } finally {
                              setIsDeleting(false);
                            }
                          }
                        });
                      }}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete All Reviews
                    </button>
                  </div>

                  {/* API Method Selection */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Reviews API Method</h3>
                    <p className="text-xs text-gray-600 mb-3">
                      Choose how to fetch Google reviews for your business
                    </p>

                    <select
                      value={settings.google_reviews_api_method}
                      onChange={(e) => setSettings(prev => ({ ...prev, google_reviews_api_method: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="simple">Simple API (Google Places - 5 reviews per sync)</option>
                      <option value="oauth">OAuth API (Business Profile - ALL reviews)</option>
                    </select>
                  </div>

                  {/* OAuth Configuration (shown when OAuth is selected) */}
                  {settings.google_reviews_api_method === 'oauth' && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md space-y-3">
                      <div className="flex items-start space-x-2.5 mb-3">
                        <AlertCircle className="h-4 w-4 text-theme-blue mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="text-xs font-medium text-blue-800">OAuth 2.0 Configuration</h3>
                          <p className="text-xs text-blue-700 mt-1">
                            This method allows you to fetch ALL your Google reviews. Requires Google Cloud Project with Business Profile API enabled.
                          </p>
                        </div>
                      </div>

                      {/* OAuth Client ID */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          <Key className="h-3.5 w-3.5 inline mr-1.5" />
                          OAuth Client ID
                        </label>
                        <input
                          type="text"
                          value={settings.google_oauth_client_id}
                          onChange={(e) => setSettings(prev => ({ ...prev, google_oauth_client_id: e.target.value }))}
                          placeholder="Enter OAuth Client ID"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* OAuth Client Secret */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          <Shield className="h-3.5 w-3.5 inline mr-1.5" />
                          OAuth Client Secret
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.google_oauth_client_secret ? "text" : "password"}
                            value={settings.google_oauth_client_secret}
                            onChange={(e) => setSettings(prev => ({ ...prev, google_oauth_client_secret: e.target.value }))}
                            placeholder="Enter OAuth Client Secret"
                            className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys(prev => ({ ...prev, google_oauth_client_secret: !prev.google_oauth_client_secret }))}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showApiKeys.google_oauth_client_secret ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Manual Account and Location IDs */}
                      <div className="mt-4 pt-4 border-t border-blue-300">
                        <h4 className="text-xs font-medium text-gray-900 mb-2">Manual Configuration (Optional)</h4>
                        <p className="text-xs text-gray-600 mb-3">
                          If automatic discovery fails, you can manually enter your Google Business Account and Location IDs
                        </p>
                        
                        <div className="space-y-3">
                          {/* Account ID */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Google Account ID
                            </label>
                            <input
                              type="text"
                              value={settings.google_account_id || ''}
                              onChange={(e) => setSettings(prev => ({ ...prev, google_account_id: e.target.value }))}
                              placeholder="e.g., 123456789012345678"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              <a 
                                href="https://business.google.com/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-theme-blue hover:underline"
                              >
                                Open Google Business Profile
                              </a> - Find ID in URL: business.google.com/?account=<strong>YOUR_ID</strong>
                            </p>
                          </div>

                          {/* Location ID */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Google Location ID
                            </label>
                            <input
                              type="text"
                              value={settings.google_location_id || ''}
                              onChange={(e) => setSettings(prev => ({ ...prev, google_location_id: e.target.value }))}
                              placeholder="e.g., 987654321098765432"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Select your location in Google Business Profile - Find ID in URL: .../locations/<strong>YOUR_ID</strong>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Redirect URI Helper */}
                      <div className="bg-green-50 border border-green-300 rounded-md p-3 mb-3">
                        <h3 className="text-xs font-medium text-green-900 mb-2">📋 Your Redirect URI (Copy This):</h3>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-white px-3 py-2 rounded text-xs border border-green-300 font-mono text-green-800">
                            {API_BASE_URL}/api/reviews/oauth/callback
                          </code>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`${API_BASE_URL}/api/reviews/oauth/callback`);
                              setMessage({ type: 'success', text: 'Redirect URI copied to clipboard!' });
                              setTimeout(() => setMessage({ type: '', text: '' }), 2000);
                            }}
                            className="px-3 py-2 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            Copy
                          </button>
                        </div>
                        <p className="text-xs text-green-700 mt-2">
                          ⚠️ You MUST add this exact URI to Google Cloud Console OAuth credentials
                        </p>
                      </div>

                      {/* OAuth Setup Instructions */}
                      <div className="bg-white border border-blue-300 rounded-md p-3">
                        <h3 className="text-xs font-medium text-gray-900 mb-2">OAuth Setup Steps:</h3>
                        <ol className="text-xs text-gray-700 list-decimal list-inside space-y-1">
                          <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">Google Cloud Console → Credentials</a></li>
                          <li>Create a new project or select existing</li>
                          <li>Enable "Google Business Profile API" and "My Business Business Information API"</li>
                          <li>Create OAuth 2.0 credentials (Web application)</li>
                          <li><strong>Click your OAuth Client ID → Add the redirect URI shown above</strong></li>
                          <li>Copy Client ID and Client Secret and paste in the fields above</li>
                          <li>Click Save on this page, then go to Reviews page to authorize</li>
                        </ol>
                      </div>
                    </div>
                  )}

                  {/* API Configuration Info */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mt-4">
                    <div className="flex items-start space-x-2.5">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="text-xs font-medium text-yellow-800">
                          {settings.google_reviews_api_method === 'oauth' ? 'OAuth Method Selected' : 'Simple API Method Selected'}
                        </h3>
                        {settings.google_reviews_api_method === 'oauth' ? (
                          <p className="text-xs text-yellow-700 mt-1">
                            OAuth method fetches ALL reviews. Complete the setup above, save settings, then authorize in Reviews page.
                          </p>
                        ) : (
                          <ol className="text-xs text-yellow-700 mt-1 list-decimal list-inside space-y-0.5">
                            <li>Create a project in Google Cloud Console</li>
                            <li>Enable the Places API</li>
                            <li>Create an API key with Places API access</li>
                            <li>Find your business Place ID from Google Maps</li>
                            <li>Enter both credentials above to enable reviews sync</li>
                          </ol>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Panel */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-start space-x-2.5">
                    <AlertCircle className="h-4 w-4 text-theme-blue mt-0.5" />
                    <div>
                      <h3 className="text-xs font-medium text-blue-800">Important Information</h3>
                      <p className="text-xs text-blue-700 mt-1">
                        Disabling Google Sync will hide all Google Connect buttons and synchronization features throughout the application. 
                        Existing Google integrations will remain connected but will not sync data until re-enabled.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AWS Storage Tab */}
            {activeTab === 'aws' && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                  <div className="mb-3 sm:mb-0">
                    <h2 className="text-base font-semibold text-gray-900">AWS Storage Settings</h2>
                    <p className="text-xs text-gray-600">
                      Configure AWS S3 storage for file uploads and media management
                    </p>
                    
                    {/* Connection Status Indicator */}
                    <div className="mt-1.5 flex items-center">
                      {connectionStatus === 'connected' && (
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs font-medium">Connected</span>
                        </div>
                      )}
                      {connectionStatus === 'disconnected' && (
                        <div className="flex items-center text-red-600">
                          <AlertCircle className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs font-medium">Not Connected</span>
                        </div>
                      )}
                      {connectionStatus === 'testing' && (
                        <div className="flex items-center text-theme-blue">
                          <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                          <span className="text-xs font-medium">Testing Connection...</span>
                        </div>
                      )}
                      {!connectionStatus && awsSettings.enabled && (
                        <div className="flex items-center text-gray-600">
                          <AlertCircle className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs font-medium">Connection Status Unknown - Test Connection</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    {/* Connect/Disconnect Button */}
                    {(!awsSettings.enabled || connectionStatus === 'disconnected') ? (
                      <button
                        onClick={handleConnect}
                        disabled={saving || !awsSettings.access_key_id || !awsSettings.secret_access_key}
                        className="w-full sm:w-auto text-white px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50 flex items-center justify-center sm:justify-start font-medium"
                        style={{ 
                          backgroundColor: saving ? '#94a3b8' : '#10b981'
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) e.target.style.backgroundColor = '#059669';
                        }}
                        onMouseLeave={(e) => {
                          if (!saving) e.target.style.backgroundColor = '#10b981';
                        }}
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Connecting...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Connect
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={handleDisconnect}
                        disabled={saving}
                        className="w-full sm:w-auto text-white px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50 flex items-center justify-center sm:justify-start font-medium"
                        style={{ 
                          backgroundColor: saving ? '#94a3b8' : '#ef4444'
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) e.target.style.backgroundColor = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          if (!saving) e.target.style.backgroundColor = '#ef4444';
                        }}
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Disconnecting...
                          </>
                        ) : (
                          <>
                            <X className="h-4 w-4 mr-2" />
                            Disconnect
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  
                  {/* AWS Enable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center space-x-3">
                      <div className="bg-orange-100 rounded-full p-2">
                        <Key className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Enable AWS S3 Storage</h3>
                        <p className="text-sm text-gray-600">
                          Enable AWS S3 for file uploads and media storage
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={awsSettings.enabled}
                        onChange={(e) => setAwsSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>

                  {/* AWS Configuration Form */}
                  <div className={`space-y-4 transition-opacity ${!awsSettings.enabled ? 'opacity-50' : ''}`}>
                    
                    {/* Access Key ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AWS Access Key ID *
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.aws_access_key ? "text" : "password"}
                          value={awsSettings.access_key_id}
                          onChange={(e) => setAwsSettings(prev => ({ ...prev, access_key_id: e.target.value }))}
                          disabled={!awsSettings.enabled}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Enter your AWS Access Key ID"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys(prev => ({ ...prev, aws_access_key: !prev.aws_access_key }))}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          disabled={!awsSettings.enabled}
                        >
                          {showApiKeys.aws_access_key ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Secret Access Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AWS Secret Access Key *
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.aws_secret_key ? "text" : "password"}
                          value={awsSettings.secret_access_key}
                          onChange={(e) => setAwsSettings(prev => ({ ...prev, secret_access_key: e.target.value }))}
                          disabled={!awsSettings.enabled}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Enter your AWS Secret Access Key"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys(prev => ({ ...prev, aws_secret_key: !prev.aws_secret_key }))}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          disabled={!awsSettings.enabled}
                        >
                          {showApiKeys.aws_secret_key ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Region */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AWS Region *
                      </label>
                      <select
                        value={awsSettings.region}
                        onChange={(e) => setAwsSettings(prev => ({ ...prev, region: e.target.value }))}
                        disabled={!awsSettings.enabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="us-east-1">US East (N. Virginia)</option>
                        <option value="us-east-2">US East (Ohio)</option>
                        <option value="us-west-1">US West (N. California)</option>
                        <option value="us-west-2">US West (Oregon)</option>
                        <option value="eu-west-1">Europe (Ireland)</option>
                        <option value="eu-west-2">Europe (London)</option>
                        <option value="eu-central-1">Europe (Frankfurt)</option>
                        <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                        <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
                        <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                      </select>
                    </div>

                    {/* Bucket Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        S3 Bucket Name *
                      </label>
                      <input
                        type="text"
                        value={awsSettings.bucket_name}
                        onChange={(e) => setAwsSettings(prev => ({ ...prev, bucket_name: e.target.value }))}
                        disabled={!awsSettings.enabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="your-bucket-name"
                      />
                    </div>

                    {/* Root Folder */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Root Folder Path
                      </label>
                      <input
                        type="text"
                        value={awsSettings.root_folder}
                        onChange={(e) => setAwsSettings(prev => ({ ...prev, root_folder: e.target.value }))}
                        disabled={!awsSettings.enabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="uploads/"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional: Specify a folder path within your bucket (e.g., "uploads/" or "media/images/")
                      </p>
                    </div>

                    {/* E-commerce Folder Path */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-commerce Image Folder Path
                      </label>
                      <input
                        type="text"
                        value={awsSettings.ecommerce_folder || 'ah/0ecom/'}
                        onChange={(e) => setAwsSettings(prev => ({ ...prev, ecommerce_folder: e.target.value }))}
                        disabled={!awsSettings.enabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="ah/0ecom/"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Folder path for product images in online ordering (e.g., "ah/0ecom/" or "ecommerce/products/")
                      </p>
                    </div>

                    {/* Max File Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maximum File Size (MB)
                      </label>
                      <input
                        type="number"
                        value={Math.round(awsSettings.max_file_size / 1048576)}
                        onChange={(e) => setAwsSettings(prev => ({ ...prev, max_file_size: parseInt(e.target.value) * 1048576 }))}
                        disabled={!awsSettings.enabled}
                        min="1"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum file size allowed for uploads (1-100 MB)
                      </p>
                    </div>

                    {/* Recycle Bin Settings */}
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4">Recycle Bin Policy</h3>
                      
                      {/* Enable Recycle Bin */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-medium text-gray-900 text-sm">Enable Recycle Bin</h4>
                          <p className="text-xs text-gray-600">
                            Move deleted files to recycle bin instead of permanent deletion
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={awsSettings.recycle_bin_enabled ?? true}
                            onChange={(e) => setAwsSettings(prev => ({ ...prev, recycle_bin_enabled: e.target.checked }))}
                            disabled={!awsSettings.enabled}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                        </label>
                      </div>

                      {/* Auto-Delete Days */}
                      <div className={awsSettings.recycle_bin_enabled === false ? 'opacity-50' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Auto-Delete After (Days)
                        </label>
                        <select
                          value={awsSettings.recycle_bin_auto_delete_days ?? 30}
                          onChange={(e) => setAwsSettings(prev => ({ ...prev, recycle_bin_auto_delete_days: parseInt(e.target.value) }))}
                          disabled={!awsSettings.enabled || awsSettings.recycle_bin_enabled === false}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="0">Never Delete (Keep Forever)</option>
                          <option value="15">15 Days</option>
                          <option value="30">30 Days</option>
                          <option value="60">60 Days</option>
                          <option value="90">90 Days</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Files in recycle bin will be permanently deleted after this period. Set to "Never" to keep items indefinitely.
                        </p>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Info Panel */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-orange-800">AWS Configuration</h3>
                      <p className="text-sm text-orange-700 mt-1">
                        Ensure your AWS credentials have the necessary S3 permissions (s3:GetObject, s3:PutObject, s3:DeleteObject) 
                        for the specified bucket. Test the connection after saving to verify the configuration.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* API Keys Tab */}
            {activeTab === 'api-keys' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">API Keys</h2>
                    <p className="text-sm text-gray-600">
                      Manage API keys for third-party integrations
                    </p>
                  </div>
                  <button
                    onClick={handleSaveApiKeys}
                    disabled={saving}
                    className="text-white px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50 flex items-center font-medium"
                    style={{ 
                      backgroundColor: saving ? '#94a3b8' : '#8b5cf6'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) e.target.style.backgroundColor = '#7c3aed';
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) e.target.style.backgroundColor = '#8b5cf6';
                    }}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save API Keys
                      </>
                    )}
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Nexsite.ai API Key */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="bg-purple-100 rounded-full p-1.5">
                          <Key className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-sm text-gray-900">Nexsite.ai API Key</h3>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Used for URL shortening service
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.nexsite_api_key ? "text" : "password"}
                          value={settings.nexsite_api_key}
                          onChange={(e) => setSettings({...settings, nexsite_api_key: e.target.value})}
                          placeholder="Enter your Nexsite.ai API Key"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeys({...showApiKeys, nexsite_api_key: !showApiKeys.nexsite_api_key})}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showApiKeys.nexsite_api_key ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">
                        This key is required to use the Short URL feature in AI Tools
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Global Placeholders Tab */}
            {activeTab === 'placeholders' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Global Placeholders</h2>
                    <p className="text-sm text-gray-600">
                      Manage global placeholder values used across email and SMS templates
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setMessage({ type: '', text: '' });
                      setEditingPlaceholder(null);
                      setPlaceholderForm({ name: '', placeholder: '', value: '', description: '' });
                      setShowPlaceholderModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Placeholder
                  </button>
                </div>

                {/* Modal will be added at the bottom */}

                {/* Placeholders List */}
                <div>
                  {loadingPlaceholders ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading placeholders...</p>
                    </div>
                  ) : placeholders.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No Global Placeholders</h4>
                      <p className="text-gray-500">Create your first global placeholder to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {placeholders.map(placeholder => (
                        <div key={placeholder.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className="font-medium text-gray-900">{placeholder.name}</h4>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {placeholder.placeholder}
                                </span>
                              </div>
                              {placeholder.description && (
                                <p className="text-sm text-gray-600 mb-2">{placeholder.description}</p>
                              )}
                              <div className="mt-2">
                                <span className="text-sm font-medium text-gray-700">Value: </span>
                                <span className="text-sm text-gray-900">{placeholder.value}</span>
                              </div>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                <span>Created: {new Date(placeholder.created_at).toLocaleDateString()}</span>
                                {placeholder.updated_at && placeholder.updated_at !== placeholder.created_at && (
                                  <span>Updated: {new Date(placeholder.updated_at).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleEditPlaceholder(placeholder)}
                                className="text-theme-blue hover:text-blue-800 text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => confirmDeletePlaceholder(placeholder.id, placeholder.name)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info Panel */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">Usage Information</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        Global placeholders can be used in email and SMS templates throughout the application. 
                        Changes to placeholder values will automatically update all templates that use them.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Feature Flags Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  
                  {/* Online Appointments Section */}
                  <div className="mb-6">
                    <div className="flex items-start justify-between py-4 border-b border-gray-200">
                      <div className="flex-1">
                        <label className="text-sm font-semibold text-gray-900 block mb-1">
                          Enable Online Appointments (Master Toggle)
                        </label>
                        <p className="text-xs text-gray-600">
                          When disabled, all online appointment features (Services, Boarding, Urgent Care) will be hidden from the navigation menu.
                        </p>
                      </div>
                      <button
                        onClick={() => setAppearanceSettings(prev => ({ 
                          ...prev, 
                          online_appointments_enabled: !prev.online_appointments_enabled 
                        }))}
                        className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          appearanceSettings.online_appointments_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            appearanceSettings.online_appointments_enabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Sub-features (indented) */}
                    {appearanceSettings.online_appointments_enabled && (
                      <div className="ml-6 space-y-4 mt-4">
                        {/* Services Toggle */}
                        <div className="flex items-start justify-between py-3 border-b border-gray-100">
                          <div className="flex-1">
                            <label className="text-sm font-medium text-gray-700 block mb-1">
                              Enable Services
                            </label>
                            <p className="text-xs text-gray-500">
                              Book Online, Manage Services, Manage Appointments, Configure Service Providers
                            </p>
                          </div>
                          <button
                            onClick={() => setAppearanceSettings(prev => ({ 
                              ...prev, 
                              services_enabled: !prev.services_enabled 
                            }))}
                            className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                              appearanceSettings.services_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                appearanceSettings.services_enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>

                        {/* Boarding Toggle */}
                        <div className="flex items-start justify-between py-3 border-b border-gray-100">
                          <div className="flex-1">
                            <label className="text-sm font-medium text-gray-700 block mb-1">
                              Enable Boarding
                            </label>
                            <p className="text-xs text-gray-500">
                              Book Online, Configure Boarding
                            </p>
                          </div>
                          <button
                            onClick={() => setAppearanceSettings(prev => ({ 
                              ...prev, 
                              boarding_enabled: !prev.boarding_enabled 
                            }))}
                            className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                              appearanceSettings.boarding_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                appearanceSettings.boarding_enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>

                        {/* Urgent Care Toggle */}
                        <div className="flex items-start justify-between py-3">
                          <div className="flex-1">
                            <label className="text-sm font-medium text-gray-700 block mb-1">
                              Enable Urgent Care
                            </label>
                            <p className="text-xs text-gray-500">
                              Book Online, Manage Appointments, check-in button, urgent care hours
                            </p>
                          </div>
                          <button
                            onClick={() => setAppearanceSettings(prev => ({ 
                              ...prev, 
                              urgent_care_enabled: !prev.urgent_care_enabled 
                            }))}
                            className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                              appearanceSettings.urgent_care_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                appearanceSettings.urgent_care_enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Records Toggle */}
                  <div className="flex items-start justify-between py-4 border-b border-gray-200">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 block mb-1">
                        Enable Records Menu
                      </label>
                      <p className="text-xs text-gray-600">
                        When disabled, the Records menu (Client & Hospital records) will be hidden from the navigation menu.
                      </p>
                    </div>
                    <button
                      onClick={() => setAppearanceSettings(prev => ({ 
                        ...prev, 
                        records_enabled: !prev.records_enabled 
                      }))}
                      className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        appearanceSettings.records_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          appearanceSettings.records_enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Online Ordering Toggle */}
                  <div className="flex items-start justify-between py-4 border-b border-gray-200">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 block mb-1">
                        Enable Online Ordering
                      </label>
                      <p className="text-xs text-gray-600">
                        When disabled, the online ordering menu will be hidden from the navigation menu.
                      </p>
                    </div>
                    <button
                      onClick={() => setAppearanceSettings(prev => ({ 
                        ...prev, 
                        online_ordering_enabled: !prev.online_ordering_enabled 
                      }))}
                      className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        appearanceSettings.online_ordering_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          appearanceSettings.online_ordering_enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* HR Menu Toggle */}
                  <div className="flex items-start justify-between py-4 border-b border-gray-200">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 block mb-1">
                        Enable HR Menu
                      </label>
                      <p className="text-xs text-gray-600">
                        When disabled, the HR menu (Employees, Timesheets, Paystubs) will be hidden from the navigation menu.
                      </p>
                    </div>
                    <button
                      onClick={() => setAppearanceSettings(prev => ({ 
                        ...prev, 
                        hr_enabled: !prev.hr_enabled 
                      }))}
                      className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        appearanceSettings.hr_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          appearanceSettings.hr_enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* AI Menu Toggle */}
                  <div className="flex items-start justify-between py-4 border-b border-gray-200">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 block mb-1">
                        Enable AI Menu
                      </label>
                      <p className="text-xs text-gray-600">
                        When disabled, the entire AI menu (AI Chatbot, AI Agents, AI Workflow, Social Media, AI Costs, AI Settings) will be hidden from the navigation menu.
                      </p>
                    </div>
                    <button
                      onClick={() => setAppearanceSettings(prev => ({ 
                        ...prev, 
                        ai_costs_enabled: !prev.ai_costs_enabled 
                      }))}
                      className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        appearanceSettings.ai_costs_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          appearanceSettings.ai_costs_enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* System Settings Menu Toggle */}
                  <div className="flex items-start justify-between py-4 last:border-0">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 block mb-1">
                        Enable System Settings Menu
                      </label>
                      <p className="text-xs text-gray-600">
                        When disabled, the System Settings menu (CMS, SMS, Email, Users, Payments) will be hidden from the navigation menu.
                      </p>
                    </div>
                    <button
                      onClick={() => setAppearanceSettings(prev => ({ 
                        ...prev, 
                        system_settings_enabled: !prev.system_settings_enabled 
                      }))}
                      className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        appearanceSettings.system_settings_enabled ? 'bg-theme-blue' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          appearanceSettings.system_settings_enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Save Button */}
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={handleSaveAppearanceSettings}
                      disabled={saving}
                      className="px-6 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      {saving ? 'Saving...' : 'Save Appearance Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="text-center py-12">
                  <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Notification Settings</h3>
                  <p className="text-gray-500">Email and system notification preferences will be configured here.</p>
                </div>
              </div>
            )}

            {/* Other Settings Tab */}
            {activeTab === 'other' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-200" style={{ backgroundColor: 'rgba(16, 185, 129, 0.05)' }}>
                    <h2 className="text-base font-semibold text-gray-900">Other Settings</h2>
                    <p className="mt-1 text-sm text-gray-600">Configure session management and other miscellaneous settings</p>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* PDF Tools Session Management */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-gray-900">PDF Tools Session Management</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Control session timeouts for PDF Tools to protect user data and manage resources
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-4">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={otherSettings.enable_session_time}
                            onChange={(e) => setOtherSettings({...otherSettings, enable_session_time: e.target.checked})}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            {otherSettings.enable_session_time ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                      </div>

                      {/* Session Time Fields - Only show when enabled */}
                      {otherSettings.enable_session_time && (
                        <div className="mt-4 space-y-4 pl-4 border-l-4 border-green-200 bg-green-50 p-4 rounded-r-lg">
                          {/* Initial Session Time */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Initial Session Time
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="120"
                                value={otherSettings.initial_session_time}
                                onChange={(e) => setOtherSettings({...otherSettings, initial_session_time: parseInt(e.target.value) || 10})}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                              <span className="text-sm text-gray-600">minutes</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Duration before the initial session timeout warning appears
                            </p>
                          </div>

                          {/* Extended Session Time */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Extended Session Time
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                max="120"
                                value={otherSettings.extended_session_time}
                                onChange={(e) => setOtherSettings({...otherSettings, extended_session_time: parseInt(e.target.value) || 10})}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                              <span className="text-sm text-gray-600">minutes</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Additional time added when user extends the session
                            </p>
                          </div>

                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-700">
                              💡 <strong>Note:</strong> These settings apply to PDF Tools session management. When enabled, users will see a countdown timer and receive warnings before their session expires.
                            </p>
                          </div>
                        </div>
                      )}

                      {!otherSettings.enable_session_time && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-xs text-yellow-700">
                            ⚠️ <strong>Warning:</strong> Session management is disabled. User files will remain in memory without automatic cleanup until page refresh.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Save Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={handleSaveOtherSettings}
                        disabled={saving}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white hover:opacity-90 transition-colors disabled:opacity-50"
                        style={{ backgroundColor: '#10b981' }}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving...' : 'Save Other Settings'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
          </div>
        </div>

        {/* Global Placeholder Modal */}
        {showPlaceholderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
              {/* Sticky Header */}
              <div className="px-6 py-4 border-b border-gray-200 rounded-t-xl flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {editingPlaceholder ? (
                      <Edit3 className="h-5 w-5 text-white" />
                    ) : (
                      <Plus className="h-5 w-5 text-white" />
                    )}
                    <h3 className="text-lg font-semibold text-white">
                      {editingPlaceholder ? 'Edit Global Placeholder' : 'Create New Global Placeholder'}
                    </h3>
                  </div>
                  <button
                    onClick={handleCancelPlaceholderForm}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Placeholder Name *
                    </label>
                    <input
                      type="text"
                      value={placeholderForm.name}
                      onChange={(e) => setPlaceholderForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                      placeholder="e.g., Website Link"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Placeholder Text *
                    </label>
                    <input
                      type="text"
                      value={placeholderForm.placeholder}
                      onChange={(e) => setPlaceholderForm(prev => ({ ...prev, placeholder: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                      placeholder="e.g., WEBSITE_LINK (brackets added automatically)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Placeholder Value *
                    </label>
                    <input
                      type="text"
                      value={placeholderForm.value}
                      onChange={(e) => setPlaceholderForm(prev => ({ ...prev, value: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                      placeholder="e.g., https://yourwebsite.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={placeholderForm.description}
                      onChange={(e) => setPlaceholderForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': 'var(--theme-primary)' }}
                      placeholder="Brief description of this placeholder"
                    />
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelPlaceholderForm}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={editingPlaceholder ? handleUpdatePlaceholder : handleCreatePlaceholder}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                  >
                    {editingPlaceholder ? (
                      <>
                        <Edit3 className="h-4 w-4 mr-2" />
                        Update Placeholder
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Placeholder
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-lg font-medium text-white">Confirm Deletion</h3>
              </div>
              
              {/* Content */}
              <div className="px-6 py-4">
                <p className="text-sm text-gray-700">{confirmMessage}</p>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    confirmAction();
                    setShowConfirmModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
                  style={{ backgroundColor: '#dc2626' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        isLoading={isDeleting}
        type="danger"
      />
      
      {/* Result Modal */}
      <ResultModal
        isOpen={resultModal.isOpen}
        onClose={() => setResultModal({ isOpen: false })}
        title={resultModal.title}
        message={resultModal.message}
        type={resultModal.type}
      />
    </div>
  );
};

export default CMSSettings;